import { useState, useEffect, useRef } from "react";
import { X, User, Bell, Shield, Palette, Globe, LogOut } from "lucide-react"
import { formatDistanceToNow, format } from 'date-fns';
import SyncedHistoryModal from "./SyncedHistoryModal";
import AddMinutesModal from "./AddMinutesModal";
import Toast from "./Toast";


const SettingsModal = ({ isOpen, onClose, onSubmit, onLogout }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [error, setError] = useState(null);
  const [isAddMinutesOpen, setIsAddMinutesOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [results, setResults] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState([]);
  const [isSyncHistoryOpen, setIsSyncHistoryOpen] = useState(false);
  const [showSyncedHistoryModal, setShowSyncedHistoryModal] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [frequency, setFrequency] = useState("");
  const [isOn, setIsOn] = useState(true);
  const [query, setQuery] = useState("");

  const showToast = (type, title, message = "", duration = 5000) => {
    setToast({ type, title, message, duration });
  };

  const closeToast = () => {
    setToast(null);
  };

  useEffect(() => {
    if (isOpen) {
      fetchSyncStatus();
      fetchNotificationsSettings();
    }
  }, [isOpen]);

  const openAddMinutes = () => {
    setIsAddMinutesOpen(true);
  };

  const closeAddMinutes = () => {
    setIsAddMinutesOpen(false);
  };

  const handleAddMinutes = async () => {
    fetchSyncStatus();
  };

  const showSyncModal = () => {
    setShowSyncedHistoryModal(true);
    setIsSyncHistoryOpen(true);
  };

  const closeSyncModal = () => {
    setShowSyncedHistoryModal(false);
    setIsSyncHistoryOpen(false);
  };

  const onLogoutHandle = () => {
    localStorage.removeItem("email");
    closeSyncModal();
    onLogout();
    onClose();
  };

  const fetchSyncStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `https://apis.metabuddy.app/flaskapp/get-triage-alerts-sync-status?env=${process.env.REACT_APP_ENV_TYPE}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP ${response.status}: ${
            errorText || "Failed to load initial questions"
          }`
        );
      }

      const data = await response.json();

      if (data.status === "success" && data.result) {
        setResults(
          data.result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        );
        setError(null);
        console.debug("data.result:", data.result);
      } else {
        throw new Error(`Invalid response: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
        const errorMessage = `Failed to load : ${err.message}`;
        setError(errorMessage);
        showToast("error", "Connection Error", err.message);
        console.error("Error loading :", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotificationsSettings = async () => {
    try {
     
      const response = await fetch(
        `https://apis.metabuddy.app/flaskapp/get-triage-alerts-notifications-settings?env=${process.env.REACT_APP_ENV_TYPE}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP ${response.status}: ${
            errorText || "Failed to load initial questions"
          }`
        );
      }

      const data = await response.json();

      if (data.status === "success" && data.result) {
        const sortedResults = data.result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setNotificationSettings(sortedResults);
        setError(null);
        console.debug("data.result:", sortedResults);
        const item = sortedResults?.[0];
        if (item) {
          setFrequency(item.frequency ?? "Every Hour");
          setName(item.name ?? "");
          setEmail(item.email ?? "");
          setQuery(item.queries?.[0] ?? "");
          setIsOn(item.is_on ?? true);
        }
        setNotificationSettings(
          data.result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        );
        
      } else {
        throw new Error(`Invalid response: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
        const errorMessage = `Failed to load : ${err.message}`;
        setError(errorMessage);
        showToast("error", "Connection Error", err.message);
        console.error("Error loading :", err);
    } finally {
      setIsLoading(false);
    }
  };

  const saveChanges = async () => {
   
    if (!name || !email || !query) {
      setError("Name, email, query can't be empty");
      showToast("error", "Hang On!", "Name, email, query can't be empty");
      return
    }
    try {
      setIsLoading(true);
      setError(null);

      const requestBody = {
        name: name,
        email: email,
        queries: [query],
        frequency: frequency,
        is_on: isOn,
        env: process.env.REACT_APP_ENV_TYPE
      };

      const response = await fetch(
        "https://apis.metabuddy.app/flaskapp/save-triage-alerts-notifications-settings",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiIzIiwianRpIjoiNTI1MDkxNGMzMWJhMzJjYTVlZWVkZmUxOGRmYTJmODI0NzBjYmZjOGJjMWNkNzYzYWYxOTdhOGM3YzU0M2EwNGFhYjQ0MzVlMjViNjdjMGQiLCJpYXQiOjE3NDgwODcwMDguNjM5OTU5LCJuYmYiOjE3NDgwODcwMDguNjM5OTYzLCJleHAiOjE3Nzk2MjMwMDguNjI2ODYxLCJzdWIiOiI5MiIsInNjb3BlcyI6W119.ABLwCRB3JqBHDgIqijsOKo4OTx4W_tzjoOlPGhBpFC5NCc6_2mz4dRkSdKlCos6_vyG2m0h1mQlFeHCglwaZ2wTdV7qcf7IfYRu5ZtZMLKTQrLEg-ZuD4ICd6D-t4OEM3oVlLM7QW_VF4WLAXxldMxKshKiBTTPLspAC0eFRGz_XVWMqbl7bevbCTrNgh3DGqp5WyJ9lOO9qNO5Vq6KsaLEEWizj_Wm3FoeRV6S2IcvLCW9gus8PjEQsEYHm2OUhQUKax-rW7g8RTdTrh2eIApT8AlBeDpcrmHmudG-EySWFVsdX_aWffAIatOzowcc4KLBJJEBRXQ1A4F3GQsPQZYXCXuPOdihD6x1vuqUVwc5aN13YJ7rGm7AqlSLEXbqr0RSRAGVfdlV8Ms_OraFHCc4juc7WbQFQ0efLnXphUUWNZgtBc_Lw79m0JuyvM70l8Hcn_fo4OlqhlOfcLTQhrK21Tc0CnXN_JiJtl9H-Y6qNErB2DoCgsXjBz0a5OPUbXQ9WfMcJSMbJJmBV1qAoybQCwSCtlqQLmV-qttVJZ-Ib3orz0ZsQs_k2-bVYwYiXBNB3zki41X0_F2bC4o-oWxiIuk1L6LDFLkcg52EDRkosNWxI7Il-po4QvWU21DojaNLWqP-REfK94W6ZqbklS4wrxCz-BzvaBtRocS4aqKg",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP ${response.status}: ${
            errorText || "Failed to load initial questions"
          }`
        );
      }

      const data = await response.json();

      if (data.status === "success") {
        setError(null);
        onSubmit();
      } else {
        throw new Error(`Invalid response: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      const errorMessage = `Failed to load questions: ${err.message}`;
      setError(errorMessage);
      showToast("error", "Connection Error", err.message);
      console.error("Error loading initial questions:", err);
    } finally {
      setIsLoading(false);
    }
  }

  if (!isOpen) return null

  return (
    <div className="p-0">
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 ">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full my-2">
        {/* Header */}

        <div className="flex items-center justify-between p-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-t-3xl">
          <h2 className="text-2xl font-bold">Settings</h2>

          <div className="flex items-center gap-3">
            <button
              onClick={onLogoutHandle}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors duration-200"
              title="Logout"
            >
              <LogOut className="w-5 h-5 text-white" />
            </button>

            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors duration-200"
              title="Close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[85vh] overflow-y-auto">
        <div className="p-8 space-y-8">
          {/* Sync Section */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">CrowdStrike Sync Status</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
              <div className="flex items-center justify-left mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Last Synced at:&nbsp;
                  {results?.[0]?.timestamp
                    ? `${formatDistanceToNow(new Date(results[0].timestamp), { addSuffix: true })} (${format(new Date(results[0].timestamp), 'MMM dd yyyy hh:mm a')})`
                    : 'N/A'}
                </label>

                <button
                  onClick={openAddMinutes}
                  className="ml-4 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Sync Again
                </button>

                <button
                  onClick={showSyncModal}
                  className="ml-4 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-blue-700"
                >
                  Show Sync History
                </button>

              </div>
              
            </div>
          </div>

          {/* Alerts Section */}
          <form>
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Receive Auto Email Alerts:</h3>
              <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" 
                    checked={isOn}
                    onChange={(e) => setIsOn(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                  </label>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Email Address(for multiple add by comma separated)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Frequecy</label>
                <select className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                 value={frequency}
                 onChange={(e) => setFrequency(e.target.value)}
                >
                  <option>Every 15 Minutes</option>
                  <option>Every 30 Minutes</option>
                  <option>Every Hour</option>
                  <option>Every 4 Hours</option>
                  <option>Every 12 Hours</option>
                  <option>Every 24 Hours</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  For what you want to be notified
                </label>
                <textarea
                  rows={4}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter your query"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                ></textarea>
              </div>
              
            </div>
          </div>
          </form>

          </div>

         
        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={saveChanges}
            disabled={isLoading}
            className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-2xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-medium"
          >
            {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  Save Changes
                </>
              )}
          </button>
        </div>
        </div>
      </div>
      
    </div>
    <SyncedHistoryModal results={results} isOpen={isSyncHistoryOpen} onClose={closeSyncModal} />
      <AddMinutesModal
        isOpen={isAddMinutesOpen}
        onClose={closeAddMinutes}
        onSubmit={handleAddMinutes}
      />
    {toast && <Toast toast={toast} onClose={closeToast} />}
    </div>
  )
}

export default SettingsModal
